import { memo, useState } from 'react'
import PropTypes from 'prop-types'

import { useFormikContext } from 'formik'

import { Autocomplete, TextField } from '@mui/material'

import { UserInfo } from '@collaborate-health/uikit'
import { useUsers } from '@tabeeb/modules/../users/hooks'

import UsersChip from './UserChip'

const UsersMultiSelect = ({ field, contentId, disabled, displayValues, endAdornment, strict }) => {
  const [text, setText] = useState('')
  const { setFieldValue } = useFormikContext()

  const { users } = useUsers({
    contentId,
    search: text,
  })

  const onInputChange = (event, value) => {
    setText(value || '')
  }

  const onChange = (_, values, reason, { option: email }) => {
    if (reason === 'createOption') {
      if (values.some((value) => value.Email === email)) {
        values.pop()
      } else {
        // eslint-disable-next-line no-param-reassign
        values[values.length - 1] = { Email: email }
      }
    }

    setFieldValue(field.name, values)
    setText('')
  }

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      freeSolo={!strict}
      fullWidth
      size='small'
      onKeyDown={(event) => {
        if (event.key === ' ' && !strict) {
          event.preventDefault()

          if (event.target.value === '') {
            return
          }

          const option = { option: event.target.value }
          onChange(event, [...field.value, option], 'createOption', option)
        }
      }}
      inputValue={text}
      clearOnEscape
      value={field.value}
      options={users}
      filterOptions={(options) => {
        return options.filter((option) => !field.value.some((user) => user.Email === option.Email))
      }}
      filterSelectedOptions
      onChange={onChange}
      onInputChange={onInputChange}
      getOptionLabel={(option) => option.Email}
      renderOption={(props, option) => (
        <li {...props} key={option.Email}>
          <UserInfo item={option} />
        </li>
      )}
      renderTags={(value, getTagProps) => {
        if (!displayValues) {
          return null
        }

        return value.map((user, index) => (
          <UsersChip key={user.Email} user={user} index={index} getTagProps={getTagProps} />
        ))
      }}
      disableClearable
      renderInput={(params) => (
        <TextField
          {...params}
          sx={{
            '& legend': { display: 'none' },
            '& fieldset': { top: 0 },
          }}
          style={{ minWidth: 100 }}
          InputProps={{ ...params.InputProps, endAdornment, style: { paddingRight: 6 } }}
          placeholder={!displayValues || field.value.length === 0 ? 'Search name or email...' : undefined}
        />
      )}
    />
  )
}

UsersMultiSelect.defaultProps = {
  disabled: false,
  displayValues: true,
  endAdornment: null,
  strict: false,
}

UsersMultiSelect.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.arrayOf(
      PropTypes.shape({
        Email: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  }).isRequired,
  contentId: PropTypes.number,
  disabled: PropTypes.bool,
  displayValues: PropTypes.bool,
  endAdornment: PropTypes.element,
  strict: PropTypes.bool,
}

export default memo(UsersMultiSelect)
